import { memo, useCallback } from 'react'
import { Currency, CurrencyAmount, Pair, Token } from '@pancakeswap/sdk'
import { useModal } from '@pancakeswap/uikit'
import { safeGetAddress } from '@/utils'
import { useTranslation } from '@pancakeswap/localization'
import { formatAmount } from '@pancakeswap/utils/formatFractions'
import { useStablecoinPriceAmount } from '@/hooks/useStablecoinPrice'
import { formatNumber } from '@pancakeswap/utils/formatBalance'
import { StablePair } from '@/views/AddLiquidity/AddStableLiquidity/hooks/useStableLPDerivedMintInfo'
import { CurrencyInput } from '@/components/ui/currency-input'
import { FiatLogo } from '@/components/Logo/CurrencyLogo'
import { useAccount } from 'wagmi'
import { useCurrencyBalance } from '@/state/wallet/hooks'
import { MaxButton } from '@/components/max-button'
import { DoubleCurrencyLogo } from '@/components/Logo'
import { Copy } from '@/components/ui/copy'
import { TriangleDownIcon } from '@radix-ui/react-icons'
import CurrencySearchModal from '../SearchModal/CurrencySearchModal'
import AddToWalletButton from '../AddToWallet/AddToWalletButton'
import { CurrencyLogo } from '@pancakeswap/widgets-internal'

interface CurrencyInputPanelProps {
  value: string | undefined
  onUserInput: (value: string) => void
  onInputBlur?: () => void
  onPercentInput?: (percent: number) => void
  onMax?: () => void
  showQuickInputButton?: boolean
  showMaxButton: boolean
  maxAmount?: CurrencyAmount<Currency>
  lpPercent?: string
  label?: string
  onCurrencySelect?: (currency: Currency) => void
  currency?: Currency | null
  disableCurrencySelect?: boolean
  hideBalance?: boolean
  pair?: Pair | StablePair | null
  otherCurrency?: Currency | null
  id: string
  showCommonBases?: boolean
  commonBasesType?: string
  showSearchInput?: boolean
  beforeButton?: React.ReactNode
  disabled?: boolean
  error?: boolean | string
  showUSDPrice?: boolean
  tokensToShow?: Token[]
  currencyLoading?: boolean
  inputLoading?: boolean
  title?: React.ReactNode
  hideBalanceComp?: boolean
}
const CurrencyInputPanel = memo(function CurrencyInputPanel({
  value,
  onUserInput,
  onInputBlur,
  onPercentInput,
  onMax,
  showQuickInputButton = false,
  showMaxButton,
  maxAmount,
  lpPercent,
  label,
  onCurrencySelect,
  currency,
  disableCurrencySelect = false,
  hideBalance = false,
  beforeButton,
  pair = null, // used for double token logo
  otherCurrency,
  id,
  showCommonBases,
  commonBasesType,
  showSearchInput,
  disabled,
  error,
  showUSDPrice,
  tokensToShow,
  currencyLoading,
  inputLoading,
  title,
  hideBalanceComp,
}: CurrencyInputPanelProps) {
  const { address: account } = useAccount()

  const selectedCurrencyBalance = useCurrencyBalance(account ?? undefined, currency ?? undefined)
  const { t } = useTranslation()

  const mode = id
  const token = pair ? pair.liquidityToken : currency?.isToken ? currency : null
  const tokenAddress = token ? safeGetAddress(token.address) : null

  const amountInDollar = useStablecoinPriceAmount(
    showUSDPrice ? currency ?? undefined : undefined,
    value !== undefined && Number.isFinite(+value) ? +value : undefined,
    {
      hideIfPriceImpactTooHigh: true,
      enabled: Boolean(value !== undefined && Number.isFinite(+value)),
    },
  )

  const [onPresentCurrencyModal] = useModal(
    <CurrencySearchModal
      onCurrencySelect={onCurrencySelect}
      selectedCurrency={currency}
      otherSelectedCurrency={otherCurrency}
      showCommonBases={showCommonBases}
      commonBasesType={commonBasesType}
      showSearchInput={showSearchInput}
      tokensToShow={tokensToShow}
      mode={mode}
    />,
  )

  const handleUserInput = useCallback(
    (val: string) => {
      onUserInput(val)
    },
    [onUserInput],
  )

  const onCurrencySelectClick = useCallback(() => {
    if (!disableCurrencySelect) {
      onPresentCurrencyModal()
    }
  }, [onPresentCurrencyModal, disableCurrencySelect])

  const balance = !hideBalance && !!currency ? formatAmount(selectedCurrencyBalance, 6) : undefined
  return (
    <CurrencyInput
      title={title}
      disabled={disabled}
      error={error as boolean}
      value={value}
      onBlur={onInputBlur}
      onUserInput={handleUserInput}
      loading={inputLoading}
      top={
        <>
          <div>
            {account && currency && selectedCurrencyBalance?.greaterThan(0) && !disabled && label !== 'To' && (
              <div className="flex items-center gap-2">
                {account && !hideBalanceComp && (
                  <button type={'button'} className={'text-xs text-gray-500'} onClick={!disabled ? onMax : undefined}>
                    {!hideBalance && !!currency
                      ? (balance?.replace('.', '')?.length || 0) > 12
                        ? balance
                        : t('Balance: %balance%', { balance: balance ?? t('Loading') })
                      : ' -'}
                  </button>
                )}
                {maxAmount?.greaterThan(0) && showMaxButton && <MaxButton onMax={onMax} />}
              </div>
            )}
          </div>
        </>
      }
      content={
        <>
          <div className={'flex items-center'}>
            {beforeButton}
            <button
              type={'button'}
              className="rounded-full py-1 pl-1.5 pr-2 border cursor-pointer shadow-xs bg-zap/20"
              onClick={onCurrencySelectClick}
            >
              <div className={'flex items-center gap-1'}>
                {pair ? (
                  <DoubleCurrencyLogo currency0={pair.token0} currency1={pair.token1} size={16} margin />
                ) : currency ? (
                  id === 'onramp-input' ? (
                    <FiatLogo currency={currency} size="24px" style={{ marginRight: '8px' }} />
                  ) : (
                    <CurrencyLogo currency={currency} />
                  )
                ) : currencyLoading ? (
                  <div className={'w-6 h-6 rounded-full bg-gray-500 animate-pulse duration-300'} />
                ) : null}
                {currencyLoading ? null : pair ? (
                  <div>
                    {pair?.token0.symbol}:{pair?.token1.symbol}
                  </div>
                ) : (
                  <div>
                    {(currency && currency.symbol && currency.symbol.length > 10
                      ? `${currency.symbol.slice(0, 4)}...${currency.symbol.slice(
                          currency.symbol.length - 5,
                          currency.symbol.length,
                        )}`
                      : currency?.symbol) || t('Select a currency')}
                  </div>
                )}
                {!currencyLoading && !disableCurrencySelect && <TriangleDownIcon />}
              </div>
            </button>
            {token && tokenAddress ? (
              <div className={'flex items-center gap-2 ml-2'}>
                <Copy content={tokenAddress} />
                <AddToWalletButton
                  data-dd-action-name="Add to wallet"
                  tokenAddress={tokenAddress}
                  tokenSymbol={token.symbol}
                  tokenDecimals={token.decimals}
                  tokenLogo={''}
                />
              </div>
            ) : null}
          </div>
        </>
      }
      bottom={
        <>
          {!!showUSDPrice && (
            <div className={'flex justify-end h-[18px]'}>
              <div className={'text-xs text-gray-400'}>
                {inputLoading ? (
                  <div className={'animate-pulse duration-800 w-8 h-3 bg-muted rounded-xs'} />
                ) : showUSDPrice && Number.isFinite(amountInDollar) ? (
                  <div>{`~${amountInDollar ? formatNumber(amountInDollar) : 0} USD`}</div>
                ) : (
                  <div className={'h-4'} />
                )}
              </div>
            </div>
          )}
        </>
      }
    />
  )
})

export default CurrencyInputPanel
